import React, { FC, useContext, useEffect } from 'react';
import { useCurrentPagesSkus } from 'src/common/hooks/useCurrentPagesSkus';
import { useMemoizedProducts } from 'src/common/hooks/useMemoizedProducts';
import { IProduct } from 'src/common/types/CatalogType';
import { ErrorTypes } from 'src/common/types/ErrorTypes';
import { ErrorFeedback } from 'src/components/molecules/ErrorFeedback';
import { ProductListLoader } from 'src/components/molecules/Loader/ProductListLoader';
import ProductListItem from 'src/components/molecules/ProductListItem';
import { CatalogContext } from 'src/contexts/Catalog';
import VisibilitySensor from 'react-visibility-sensor';

import { useProductImpressions } from './ProductsListServices';
import { getCurrentPagesNumber } from 'src/services/googleAnalytics/enhancedEcommerce';

export interface IProductsListProps {
  onSelectProduct: (product: IProduct, productPosition: number) => void;
  isVisible?: boolean;
}

export const ProductsList: FC<IProductsListProps> = ({
  onSelectProduct,
  isVisible = true,
}) => {
  const [{ page, uf, exhibition }] = useContext(CatalogContext);
  const skus = useCurrentPagesSkus(exhibition.currentPagesData);
  const { products, isLoading, hasError } = useMemoizedProducts(skus, uf, page);

  const { addProductImpression, clearProductImpressions } =
    useProductImpressions(getCurrentPagesNumber(exhibition.currentPagesData));

  useEffect(() => {
    clearProductImpressions();
  }, [page]);

  if (!isVisible) {
    return null;
  }

  const selectProductHandler =
    (productPosition: number) => (product: IProduct) => {
      onSelectProduct(product, productPosition);
    };

  return (
    <>
      {isLoading && <ProductListLoader quantity={skus.length} />}
      {hasError && <ErrorFeedback type={ErrorTypes.productNotFound} />}
      {products.map((product, productPosition) => (
        <VisibilitySensor
          key={product.sku}
          partialVisibility={true}
          onChange={(isElemVisible: boolean) => {
            isElemVisible && addProductImpression(product, productPosition);
          }}
        >
          <ProductListItem
            onSelectProduct={selectProductHandler(productPosition)}
            product={product}
          />
        </VisibilitySensor>
      ))}

      {!products.length && !hasError && !isLoading && (
        <ErrorFeedback type={ErrorTypes.emptyProductsList} />
      )}
    </>
  );
};
